import type { FC } from 'react';
import React from 'react';
import { useTheme } from '@mui/material/styles';
import { Skeleton, Stack, styled, Typography } from '@mui/material';
import { Box, PdfFileIcon, Tooltip } from '@pflegenavi/web-components';
import { useTranslation } from 'react-i18next';

interface NavigationBarImagePreviewProps {
  imageUrl: string;
  fileName: string;
  isPdf: boolean;
  isImage: boolean;
  selected: boolean;
  onImageSelect: (imageUrl: string) => void;
  isLoading: boolean;
}

export const NavigationBarImagePreview: FC<NavigationBarImagePreviewProps> = ({
  imageUrl,
  fileName,
  selected,
  isPdf,
  isImage,
  onImageSelect,
  isLoading,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <SmallImagePreview
        selected={selected}
        pinkColor={theme.palette.secondary.dark}
        onClick={() => onImageSelect(imageUrl)}
      >
        <Skeleton variant="rectangular" width={60} height={60} />
      </SmallImagePreview>
    );
  }

  if (isPdf) {
    return (
      <Tooltip title={fileName} placement="top">
        <SmallImagePreview
          selected={selected}
          pinkColor={theme.palette.secondary.dark}
          onClick={() => onImageSelect(imageUrl)}
        >
          <SmallPdfPreview selected={selected} />
        </SmallImagePreview>
      </Tooltip>
    );
  } else if (isImage) {
    return (
      <SmallImagePreview
        selected={selected}
        pinkColor={theme.palette.secondary.dark}
        onClick={() => onImageSelect(imageUrl)}
      >
        <img
          alt=""
          src={imageUrl}
          style={{
            width: '120%',
          }}
        />
      </SmallImagePreview>
    );
  } else {
    return (
      <SmallImagePreview
        selected={selected}
        pinkColor={theme.palette.secondary.dark}
        onClick={() => onImageSelect(imageUrl)}
      >
        <Typography
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          fontSize={10}
          textAlign={'center'}
        >
          {t('common.file-type-not-supported-no-file-type')}
        </Typography>
      </SmallImagePreview>
    );
  }
};

const SmallPdfPreview: FC<{ selected: boolean }> = ({ selected }) => {
  const theme = useTheme();

  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      sx={{
        pointerEvents: 'none',
        border: selected ? '0px solid' : '2px dashed',
        borderColor: selected ? 'none' : theme.palette.grey['300'],
        borderRadius: 2,
        objectFit: 'cover',
        width: '100%',
        height: '100%',
      }}
    >
      <img
        alt="pdf file icon"
        src={PdfFileIcon}
        style={{
          height: 38,
        }}
      />
    </Stack>
  );
};

interface SmallImagePreviewProps {
  selected: boolean;
  pinkColor: string;
}

const SmallImagePreview = styled(Box)<SmallImagePreviewProps>(
  ({ selected, pinkColor }) =>
    ({ theme }) =>
      theme.unstable_sx({
        borderRadius: 2,
        margin: 0.5,
        filter: `grayscale(${selected ? '0' : '30'})`,
        height: 60,
        width: 60,
        bgcolor: 'white',
        border: selected ? '3px solid' : 0,
        borderColor: selected ? pinkColor : 'none',
        overflow: 'hidden',
        '&:hover': {
          bgcolor: 'white',
          opacity: 0.7,
        },
      })
);
